import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import TagFilter from "../components/TagFilter";
import BlogSkeleton from "../components/BlogSkeleton";

declare var require: any;

interface BlogPostMetadata {
    slug: string;
    title: string;
    date: string;
    tags: string[];
}

const extractFrontmatter = (markdown: string) => {
    const frontmatterRegex = /^---\n([\s\S]*?)\n---/;
    const match = markdown.match(frontmatterRegex);

    if (match && match[1]) {
        const frontmatter = match[1]
            .split("\n")
            .reduce((acc: { [key: string]: any }, line: string) => {
                const [key, value] = line.split(": ");
                if (key && value) {
                    try {
                        // Handle JSON parsing for arrays (like tags)
                        acc[key.trim()] = JSON.parse(value.trim());
                    } catch {
                        // Fallback for plain strings
                        acc[key.trim()] = value.trim();
                    }
                }
                return acc;
            }, {});
        return {
            data: frontmatter,
            content: markdown.replace(frontmatterRegex, "").trim(),
        };
    }
    return { data: {}, content: markdown };
};

const Bloglist: React.FC = () => {
    const [posts, setPosts] = useState<BlogPostMetadata[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const postsPerPage = 6;

    useEffect(() => {
        const fetchBlogPosts = async () => {
            setLoading(true);
            try {
                const context = (require as any).context("../content/blogs", false, /\.md$/);
                const postPromises = context.keys().map(async (filename: string) => {
                    const fileUrl = context(filename);
                    const response = await fetch(fileUrl);
                    const rawText = await response.text();
                    console.log(`Fetched ${filename}:`, rawText.substring(0, 100));
                    const { data } = extractFrontmatter(rawText);
                    const slug = filename.replace("./", "").replace(".md", "");

                    if (slug && data.title && data.date && data.tags) {
                        return {
                            slug,
                            title: data.title,
                            date: data.date,
                            tags: data.tags as string[],
                        };
                    }
                    return null;
                });

                const loadedPosts = (await Promise.all(postPromises)).filter(Boolean) as BlogPostMetadata[];
                setPosts(loadedPosts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
            } catch (error) {
                console.error("Error fetching blog posts:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogPosts();
    }, []);

    const allTags = Array.from(new Set(posts.flatMap((post) => post.tags))).sort();

    const filteredPosts = posts.filter((post) => {
        const matchesTag = selectedTag ? post.tags.includes(selectedTag) : true;
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesTag && matchesSearch;
    });

    const indexOfLastPost = currentPage * postsPerPage;
    const indexOfFirstPost = indexOfLastPost - postsPerPage;
    const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);

    const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

    return (
        <div className="container mx-auto p-4 max-w-6xl pt-24">
            <h1 className="text-4xl font-bold mb-8 text-terminal-accent font-mono tracking-tighter">
                {">"} ls ./content/blogs
            </h1>

            <div className="mb-12">
                <div className="flex items-center space-x-2 bg-terminal-subtle/20 border border-terminal-subtle p-3 rounded-lg max-w-md mb-8 group focus-within:border-terminal-text transition-colors shadow-[0_0_10px_rgba(0,255,65,0.05)]">
                    <span className="text-terminal-text font-mono">~/search $</span>
                    <input
                        type="text"
                        placeholder="grep 'keyword'..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="bg-transparent border-none outline-none text-terminal-text font-mono w-full placeholder:text-terminal-subtle/50"
                    />
                    <span className="animate-pulse text-terminal-text font-bold">_</span>
                </div>

                <TagFilter
                    tags={allTags}
                    selectedTag={selectedTag}
                    onTagSelect={(tag) => {
                        setSelectedTag(tag);
                        setCurrentPage(1);
                    }}
                />
            </div>

            {loading ? (
                <BlogSkeleton />
            ) : currentPosts.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed border-terminal-subtle rounded-xl">
                    <p className="text-terminal-subtle font-mono text-xl mb-4">Error 404: Posts not found.</p>
                    <button
                        onClick={() => { setSearchTerm(""); setSelectedTag(null); }}
                        className="text-terminal-text hover:text-terminal-accent font-mono underline"
                    >
                        {">"} reset_query
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {currentPosts.map((post) => (
                        <Link
                            to={`/blogs/${post.slug}`}
                            key={post.slug}
                            className="block bg-terminal-background border border-terminal-subtle rounded-lg overflow-hidden hover:border-terminal-text hover:shadow-[0_0_15px_rgba(0,255,65,0.1)] transition-all duration-300 group"
                        >
                            <div className="p-6">
                                <h2 className="text-2xl font-semibold mb-2 text-terminal-accent group-hover:text-terminal-text transition-colors">{post.title}</h2>
                                <p className="text-terminal-subtle text-sm mb-4 font-mono">{new Date(post.date).toLocaleDateString()}</p>
                                <div className="flex flex-wrap gap-2">
                                    {post.tags.map((tag: string) => (
                                        <span key={tag} className="text-terminal-text text-xs font-mono">
                                            #{tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {filteredPosts.length > postsPerPage && (
                <div className="flex justify-center space-x-2">
                    {Array.from({ length: Math.ceil(filteredPosts.length / postsPerPage) }, (_, i) => (
                        <button
                            key={i + 1}
                            onClick={() => paginate(i + 1)}
                            className={`px-4 py-2 rounded ${currentPage === i + 1
                                ? "bg-terminal-text text-terminal-background"
                                : "bg-terminal-subtle text-terminal-accent hover:bg-terminal-text hover:text-terminal-background"
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Bloglist;
